import React from "react";
import { Box, Button, Typography } from "@material-ui/core";
import clsx from "clsx";
import { useHistory } from "react-router-dom";

import {
  ActivitiesEntryTypeEnum,
  ActivitiesDetailsProperties,
  CLICKED_SIMILAR_ACTIVITIES,
  ExperienceAvailability,
  ExperiencePrice,
  ExperiencePricingCategoryEnum,
  ExperiencesCancellationSummary,
  MediaAsset,
  MediaAssetKindEnum,
  RewardsAccount,
  VIEWED_COMPARE_ITINERARIES,
} from "redmond";
import {
  ActionButton,
  AvailabilityCarousel,
  formatDuration,
  Icon,
  IconName,
  Tag,
} from "halifax";
import dayjs from "dayjs";
import Skeleton from "react-loading-skeleton";
import { getExperiencesShopQuery } from "@capone/common";

import styles from "./styles.module.scss";
import {
  AVG_REVIEWS,
  CANCELLATION,
  COMPARE_ITINERARY,
  CONTINUE_TO_CHECKOUT,
  CURRENT,
  DURATION,
  NON_REFUNDABLE,
  PRICE,
  PRICE_TITLE,
  RATING,
  VIEW_DETAILS,
} from "../../../../textConstants";
import { EarnTagText } from "../../../../../common";
import { PATH_BOOK, PATH_SHOP } from "../../../../../../utils/paths";
import { FREE_CANCELLATION } from "../../../../../common/textConstants";
import { trackEvent } from "../../../../../../api/v0/analytics/trackEvent";

export interface IExperiencesSimilarCardProps {
  isMobile?: boolean;
  experiencesAvailabilityInfo?: ExperienceAvailability;
  variant?: "row" | "card";
  rewardsAccount?: RewardsAccount;
  selectedExperienceIdValue?: string;
  isSkeleton: boolean;
  fromDate: Date | null;
  untilDate: Date | null;
  onCompareClick?: (selectedComparedExpId: string) => void;
  disableCheckout?: boolean;
  locationLabel: string | null;
  similarIndex?: number;
  activityDetailsProperties?: {
    properties: ActivitiesDetailsProperties;
    encryptedProperties: string[];
  };
  entryType?: ActivitiesEntryTypeEnum;
  entryTypesAll?: ActivitiesEntryTypeEnum[];
}

export const ExperiencesSimilarCard = (
  props: IExperiencesSimilarCardProps,
): React.ReactElement => {
  const {
    isMobile,
    experiencesAvailabilityInfo,
    variant = "card",
    rewardsAccount,
    selectedExperienceIdValue,
    fromDate,
    untilDate,
    isSkeleton,
    onCompareClick,
    disableCheckout,
    locationLabel,
    similarIndex,
    activityDetailsProperties,
    entryType,
    entryTypesAll,
  } = props;
  const history = useHistory();

  const {
    name,
    rating,
    duration,
    bestPrice,
    pricingCategory,
    coverImage,
    additionalImages,
    cancellationSummary,
    id,
  } = experiencesAvailabilityInfo ?? {
    name: "",
    rating: { numberOfReviews: 0, reviewAverage: 0 },
    duration: 0,
    bestPrice: {
      fiat: {
        currencyCode: "",
        currencySymbol: "",
        value: 0,
      },
      rewards: {},
      accountSpecific: {},
    },
    pricingCategory: {
      ExperiencePricingCategory: ExperiencePricingCategoryEnum.PerPerson,
    },
    coverImage: "",
    additionalImages: [],
  };

  const experiencePrice: ExperiencePrice = {
    price: bestPrice,
    pricingCategory,
  };

  const coverMedia: MediaAsset = {
    kind: MediaAssetKindEnum.Image,
    url: coverImage,
  };

  const mediaArray: MediaAsset[] = [coverMedia];
  if (variant === "row") {
    additionalImages?.forEach((additionalImage) => {
      mediaArray.push({
        kind: MediaAssetKindEnum.Image,
        url: additionalImage,
      } as MediaAsset);
    });
  }

  const isMainExpCard = selectedExperienceIdValue === id?.value;

  return (
    <Box
      className={clsx({
        mobile: isMobile,
        [styles.card]: variant === "card",
        // TODO row style
        // [styles.row]: variant === "row",
      })}
    >
      <Box className={styles["carousel-container"]}>
        {rewardsAccount && !isSkeleton && (
          <Box className={styles["tag-container"]}>
            <Tag
              className={styles["earn-tag"]}
              label={<EarnTagText rewardsAccount={rewardsAccount} />}
            />
          </Box>
        )}
        {isMainExpCard && !isSkeleton ? (
          <div className={styles["current-tag"]}>{CURRENT}</div>
        ) : null}
        <AvailabilityCarousel
          media={mediaArray}
          hideArrows={variant === "card"}
          isSkeleton={isSkeleton}
        />
      </Box>
      <Box className={styles["info-container"]}>
        <Box className={styles["info-row-container"]}>
          <Box className={styles["experience-name-container"]}>
            {isSkeleton ? (
              <Skeleton className={styles["skeleton-container"]} />
            ) : (
              name
            )}
          </Box>
        </Box>
        <Box className={styles["info-row-container"]}>
          <Box
            className={clsx(
              styles["info-title-icon-container"],
              styles["reviews-title-icon-container"],
            )}
          >
            <Icon name={IconName.TripAdvisorIcon} />
            <Typography variant="body1">{RATING}</Typography>
          </Box>
          {isSkeleton ? (
            <Skeleton className={styles["skeleton-container"]} />
          ) : (
            <Typography variant="h6">
              {AVG_REVIEWS({
                avg: rating.reviewAverage,
                numberOfReviews: rating.numberOfReviews,
              })}
            </Typography>
          )}
        </Box>
        <Box className={styles["info-row-container"]}>
          <Box className={styles["info-title-icon-container"]}>
            <Icon name={IconName.ClockIconThin} />
            <Typography variant="body1">{DURATION}</Typography>
          </Box>
          {isSkeleton ? (
            <Skeleton className={styles["skeleton-container"]} />
          ) : (
            <Typography variant="h6">
              {formatDuration(dayjs.duration({ minutes: duration }))}
            </Typography>
          )}
        </Box>
        <Box className={styles["info-row-container"]}>
          <Box className={styles["info-title-icon-container"]}>
            <Icon name={IconName.CalendarCancellationIcon} />
            <Typography variant="body1">{CANCELLATION}</Typography>
          </Box>
          {isSkeleton ? (
            <Skeleton className={styles["skeleton-container"]} />
          ) : (
            <Typography
              variant="h6"
              className={clsx({
                [styles["free-cancellation-label"]]:
                  cancellationSummary ===
                  ExperiencesCancellationSummary.FreeCancellation,
              })}
            >
              {cancellationSummary ===
              ExperiencesCancellationSummary.FreeCancellation
                ? FREE_CANCELLATION
                : NON_REFUNDABLE}
            </Typography>
          )}
        </Box>
        <Box className={styles["info-row-container"]}>
          <Box className={styles["info-title-icon-container"]}>
            <Icon name={IconName.MoneyOutlineThin} />
            <Typography variant="body1">{PRICE}</Typography>
          </Box>
          {isSkeleton ? (
            <Skeleton className={styles["skeleton-container"]} />
          ) : (
            <Typography variant="h6">
              {PRICE_TITLE(experiencePrice.price, pricingCategory)}
            </Typography>
          )}
        </Box>
        {isMainExpCard && !isSkeleton ? (
          <ActionButton
            className={styles["checkout-cta-button"]}
            onClick={() =>
              history.push(`${PATH_BOOK}${history.location.search}`)
            }
            message={CONTINUE_TO_CHECKOUT}
            disabled={disableCheckout}
          />
        ) : (
          <Box>
            <Button
              variant="outlined"
              color="primary"
              className={styles["view-details-button"]}
              onClick={() => {
                if (id) {
                  if (activityDetailsProperties) {
                    trackEvent({
                      eventName: CLICKED_SIMILAR_ACTIVITIES,
                      properties: {
                        ...activityDetailsProperties.properties,
                        product_id_clicked: id.value,
                        product_name_clicked: name,
                        price_clicked: bestPrice?.fiat?.value,
                        duration_clicked: duration,
                        rating_clicked: rating?.reviewAverage,
                        product_index_clicked:
                          similarIndex !== undefined
                            ? similarIndex + 1
                            : undefined,
                      },
                      encryptedProperties:
                        activityDetailsProperties.encryptedProperties,
                    });
                  }
                  window.open(
                    `${PATH_SHOP}${getExperiencesShopQuery(
                      id,
                      fromDate,
                      untilDate,
                      locationLabel,
                      undefined,
                      undefined,
                      ActivitiesEntryTypeEnum.SIMILAR_ACTIVITIES,
                      entryTypesAll ?? (entryType ? [entryType] : []),
                    )}`,
                    "_blank",
                  );
                }
              }}
            >
              {VIEW_DETAILS}
            </Button>
            <Button
              variant="text"
              color="primary"
              className={styles["compare-itinerary-button"]}
              onClick={() => {
                if (activityDetailsProperties) {
                  trackEvent({
                    eventName: VIEWED_COMPARE_ITINERARIES,
                    properties: {
                      ...activityDetailsProperties.properties,
                      product_id_clicked: id?.value,
                      product_name_clicked: name,
                    },
                    encryptedProperties:
                      activityDetailsProperties.encryptedProperties,
                  });
                }
                if (onCompareClick) {
                  onCompareClick(id?.value || "");
                }
              }}
            >
              {COMPARE_ITINERARY}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
